import i18n from 'i18next';
import { DEFAULT_LOCALE, EN_LOCALE } from './routing';

const loadedLocales = new Set();
const loadedNamespaces = new Set();

export const I18N_NAMESPACE_FILES = {
  guides: 'guides',
  landingPages: 'landing-pages',
  faq: 'faq',
};

function normalizeLocale(locale) {
  return locale === EN_LOCALE ? EN_LOCALE : DEFAULT_LOCALE;
}

function namespaceKey(locale, namespace) {
  return `${normalizeLocale(locale)}:${namespace}`;
}

export function isLocaleBundleLoaded(locale) {
  return loadedLocales.has(normalizeLocale(locale));
}

export function isI18nNamespaceLoaded(locale, namespace) {
  return loadedNamespaces.has(namespaceKey(locale, namespace));
}

export async function loadLocaleBundle(locale) {
  const lng = normalizeLocale(locale);
  if (loadedLocales.has(lng)) {
    return lng;
  }

  const [coreModule, guidesTeasersModule, landingTeasersModule] = await Promise.all([
    import(`./locales/${lng}.json`),
    import(`./locales/guides-teasers-${lng}.json`),
    import(`./locales/landing-pages-teasers-${lng}.json`),
  ]);

  i18n.addResourceBundle(
    lng,
    'translation',
    {
      ...coreModule.default,
      guidesTeasers: guidesTeasersModule.default,
      landingPagesTeasers: landingTeasersModule.default,
    },
    true,
    true
  );

  loadedLocales.add(lng);
  return lng;
}

export async function loadI18nNamespace(locale, namespace) {
  const lng = normalizeLocale(locale);
  const filePrefix = I18N_NAMESPACE_FILES[namespace];
  if (!filePrefix) {
    throw new Error(`Unknown i18n namespace: ${namespace}`);
  }

  const key = namespaceKey(lng, namespace);
  if (loadedNamespaces.has(key)) {
    return lng;
  }

  const module = await import(`./locales/${filePrefix}-${lng}.json`);
  i18n.addResourceBundle(
    lng,
    'translation',
    { [namespace]: module.default },
    true,
    true
  );

  loadedNamespaces.add(key);
  return lng;
}

export async function loadI18nNamespaces(locale, namespaces) {
  await Promise.all(namespaces.map((namespace) => loadI18nNamespace(locale, namespace)));
  return normalizeLocale(locale);
}
